import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { api } from "@/lib/api";
import VideoCard from "@/components/VideoCard";
import NativeBannerAd from "@/components/ads/NativeBannerAd";

const PAGE_SIZE = 10;

export const Browse = () => {
  const [params, setParams] = useSearchParams();
  const page = parseInt(params.get("page") || "1", 10);
  const q = params.get("q") || "";

  const [data, setData] = useState({ items: [], total: 0 });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api
      .get("/videos", {
        params: { q, sort: "latest", skip: (page - 1) * PAGE_SIZE, limit: PAGE_SIZE },
      })
      .then((r) => setData(r.data))
      .finally(() => setLoading(false));
  }, [page, q]);

  const totalPages = Math.max(1, Math.ceil(data.total / PAGE_SIZE));
  const goTo = (p) => {
    const next = new URLSearchParams(params);
    next.set("page", String(p));
    setParams(next);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const pages = (() => {
    const arr = [];
    const start = Math.max(1, page - 2);
    const end = Math.min(totalPages, start + 4);
    for (let i = start; i <= end; i++) arr.push(i);
    return arr;
  })();

  return (
    <div className="mx-auto max-w-7xl px-4 pt-6 md:px-12 md:pt-16">
      <div className="mb-8 animate-fade-up md:mb-10">
        <div className="text-[10px] uppercase tracking-[0.32em] text-[#e60039] md:text-xs">
          The Library
        </div>
        <h1
          data-testid="browse-title"
          className="mt-2 font-display text-3xl tracking-tight text-[#f2f2f2] sm:text-4xl md:mt-3 md:text-5xl lg:text-6xl"
        >
          {q ? (
            <>
              Results for <span className="italic text-[#e60039]">"{q}"</span>
            </>
          ) : (
            <>
              Browse <span className="italic text-[#e60039]">all videos</span>
            </>
          )}
        </h1>
        <p className="mt-2 text-xs text-[#a3a3a3] md:mt-3 md:text-sm">
          {data.total} {data.total === 1 ? "video" : "videos"} · {PAGE_SIZE} per page
        </p>
      </div>

      <div
        data-testid="browse-grid"
        className="grid grid-cols-1 gap-6 sm:grid-cols-2 sm:gap-8 lg:grid-cols-3 xl:grid-cols-4"
      >
        {loading &&
          Array.from({ length: PAGE_SIZE }).map((_, i) => (
            <div key={i} className="aspect-video w-full skeleton rounded-sm" />
          ))}
        {!loading && data.items.map((v) => <VideoCard key={v.id} video={v} />)}
      </div>

      <NativeBannerAd />

      {!loading && data.items.length === 0 && (
        <div className="rounded-sm border border-dashed border-[#1a1a1a] p-12 text-center text-[#666]">
          No videos found.
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <nav
          data-testid="browse-pagination"
          className="mt-10 flex flex-wrap items-center justify-center gap-1 pb-10 md:mt-14"
        >
          <button
            data-testid="page-prev"
            onClick={() => goTo(Math.max(1, page - 1))}
            disabled={page === 1}
            aria-label="Previous page"
            className="inline-flex h-9 items-center gap-1 rounded-sm border border-[#1a1a1a] bg-[#0f0f0f] px-2.5 text-[10px] uppercase tracking-[0.2em] text-[#f2f2f2] transition-colors hover:bg-[#1a1a1a] disabled:opacity-30 sm:px-3 sm:text-xs"
          >
            <ChevronLeft className="h-3.5 w-3.5" />
            <span className="hidden sm:inline">Prev</span>
          </button>
          {pages.map((p) => (
            <button
              key={p}
              data-testid={`page-${p}`}
              onClick={() => goTo(p)}
              className={`h-9 min-w-9 rounded-sm border px-2 text-sm transition-colors sm:px-3 ${
                p === page
                  ? "border-[#e60039] bg-[#e60039] text-white"
                  : "border-[#1a1a1a] bg-[#0f0f0f] text-[#f2f2f2] hover:bg-[#1a1a1a]"
              }`}
            >
              {p}
            </button>
          ))}
          <button
            data-testid="page-next"
            onClick={() => goTo(Math.min(totalPages, page + 1))}
            disabled={page === totalPages}
            aria-label="Next page"
            className="inline-flex h-9 items-center gap-1 rounded-sm border border-[#1a1a1a] bg-[#0f0f0f] px-2.5 text-[10px] uppercase tracking-[0.2em] text-[#f2f2f2] transition-colors hover:bg-[#1a1a1a] disabled:opacity-30 sm:px-3 sm:text-xs"
          >
            <span className="hidden sm:inline">Next</span>
            <ChevronRight className="h-3.5 w-3.5" />
          </button>
        </nav>
      )}
    </div>
  );
};

export default Browse;
